import { Box, Spinner } from '@chakra-ui/react';
import { Button, HStack, Pressable, Text, VStack } from 'components/ui';
import React, { useState, useMemo, useEffect } from 'react';
import {
  exceedMonthBound,
  formatTime,
  formatMonthYear,
  formatSelectedDate,
  formatTimeZoneLabel,
  getDateAvailability,
  getEarliestSelectableDay,
  getMonthWeeks,
  getStartOfMonth,
  isPastWeek,
  precedesCurrentMonth,
} from './utils';
import { AvailabilityEntry, ProviderTime } from '../../api/types';
import { TRACKING_SCROLL_APPOINTMENT_DATES } from '../OnboardingForm/constants';
import { useTracking } from '../../hooks/useTracking';
import { DateSelectionSkeleton } from './DateSelectionSkeleton';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Globe,
} from 'lucide-react';

const DISABLED_COLOR = '#C3CFD2';
const WEEKDAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const INITIAL_TIMES_COUNT = 5;
// The number of weeks users can advance to
// starting from today. Matches the 4 month limit in app
const MAX_WEEKS_COUNT = 16;
const BLOCK_GAP = '16px';

type MonthArrowProps = {
  children: React.ReactNode;
  isHidden: boolean;
  label: string;
  onClick: () => void;
};

// The header is a three slot row. Keeping the empty slot holds the month name
// in the centre, where unmounting an arrow would shift it.
const MonthArrow = ({
  children,
  isHidden,
  label,
  onClick,
}: MonthArrowProps) => (
  <Pressable
    alignItems={'center'}
    aria-label={label}
    bg={'white'}
    borderColor={'fieldBorder.200'}
    borderWidth={'1.5px'}
    color={'secondary.200'}
    flexShrink={0}
    h={'36px'}
    justifyContent={'center'}
    onClick={onClick}
    pointerEvents={isHidden ? 'none' : 'auto'}
    rounded={'pill'}
    visibility={isHidden ? 'hidden' : 'visible'}
    w={'36px'}
  >
    {children}
  </Pressable>
);

interface DateTimePickerProps {
  availability: AvailabilityEntry[];
  firstAvailableDay: Date;
  isFetchingAvailability?: boolean;
  onNavigateMonth?: (monthStart: Date) => void;
  onSelectDate: (selectedDate: Date) => void;
  onSelectTime: (selectedTime: string | ProviderTime | null) => void;
  selectedDate: Date;
  selectedTime: string | ProviderTime | null;
  timezone: string | undefined;
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  availability,
  firstAvailableDay,
  isFetchingAvailability,
  onNavigateMonth,
  onSelectDate,
  onSelectTime,
  selectedDate,
  selectedTime,
  timezone,
}) => {
  const { track } = useTracking();
  const [visibleMonth, setVisibleMonth] = useState<Date>(
    getStartOfMonth(firstAvailableDay)
  );
  const [isMonthExpanded, setIsMonthExpanded] = useState(false);
  const [isShowingAllTimes, setIsShowingAllTimes] = useState(false);

  const handleDateChange = (date: Date) => {
    onSelectDate(date);
    onSelectTime(null);
  };

  const handleMonthNavigation = (direction: 'prev' | 'next') => {
    const offset = direction === 'next' ? 1 : -1;
    const nextMonth = new Date(
      visibleMonth.getFullYear(),
      visibleMonth.getMonth() + offset,
      1
    );

    track(TRACKING_SCROLL_APPOINTMENT_DATES, {
      direction: `scrolled to ${
        direction === 'next' ? 'following' : 'previous'
      } month`,
      startDate: nextMonth.toString(),
    });

    // Every month opens on one week, so the chevron always has a month to open.
    setIsMonthExpanded(false);
    setVisibleMonth(nextMonth);
    onNavigateMonth?.(nextMonth);
  };

  // Dropping the weeks that are gone keeps the grid to the rows worth booking.
  const bookableWeeks = useMemo(
    () => getMonthWeeks(visibleMonth).filter((week) => !isPastWeek(week)),
    [visibleMonth]
  );

  // The week holding the selected day is also the week the user expanded from.
  // Falls back to the first week with times, for a month with no day picked yet.
  const collapsedWeekIndex = useMemo(() => {
    const selectedWeek = bookableWeeks.findIndex((week) =>
      week.some((date) => date?.toDateString() === selectedDate.toDateString())
    );
    if (selectedWeek > -1) return selectedWeek;

    const firstWeekWithTimes = bookableWeeks.findIndex((week) =>
      week.some(
        (date) => !!date && !!getDateAvailability(availability, date).length
      )
    );

    return Math.max(firstWeekWithTimes, 0);
  }, [availability, bookableWeeks, selectedDate]);

  const visibleDays = isMonthExpanded
    ? bookableWeeks.flat()
    : (bookableWeeks[collapsedWeekIndex] ?? []);
  const canExpand = bookableWeeks.length > 1;
  // An arrow with nowhere to go is hidden. The forward one waits for the open
  // month so the chevron leads, and appears on a month too short to expand,
  // which has no chevron to lead with.
  const isPrevMonthHidden = precedesCurrentMonth(visibleMonth);
  const isNextMonthHidden =
    (!isMonthExpanded && canExpand) ||
    exceedMonthBound(visibleMonth, MAX_WEEKS_COUNT);

  // the time slots available of the selected date
  const availableTimes = useMemo(() => {
    const selectedDateAvailability = getDateAvailability(
      availability,
      selectedDate
    );

    return selectedDateAvailability.map((time) => ({
      time,
      label: formatTime(typeof time === 'string' ? time : time.time, timezone),
    }));
  }, [availability, selectedDate, timezone]);

  const visibleTimes = isShowingAllTimes
    ? availableTimes
    : availableTimes.slice(0, INITIAL_TIMES_COUNT);
  const selectedDay = selectedDate.toDateString();
  const timeZoneLabel = formatTimeZoneLabel(timezone);

  // A new day brings its own list, so it starts short again. The key is the day
  // string, which survives the date object being recreated on every fetch.
  useEffect(() => {
    setIsShowingAllTimes(false);
  }, [selectedDay]);

  // Only fires when the first available day really changes, because the hook
  // holds it as a timestamp. Paging months therefore keeps the month on screen.
  useEffect(() => {
    setVisibleMonth(getStartOfMonth(firstAvailableDay));
  }, [firstAvailableDay]);

  return (
    <VStack gap={BLOCK_GAP} w={'100%'}>
      {/* Month navigation */}
      <HStack
        alignItems={'center'}
        gap={'10px'}
        justifyContent={'space-between'}
        w={'100%'}
      >
        <MonthArrow
          isHidden={isPrevMonthHidden}
          label={'Previous month'}
          onClick={() => handleMonthNavigation('prev')}
        >
          <ChevronLeft size={15} />
        </MonthArrow>

        <Text
          color={'secondary.500'}
          flex={1}
          fontSize={'15.5px'}
          fontWeight={700}
          textAlign={'center'}
        >
          {formatMonthYear(visibleMonth)}
        </Text>

        {isFetchingAvailability ? (
          <Spinner size={'sm'} height={'36px'} width={'36px'} />
        ) : (
          <MonthArrow
            isHidden={isNextMonthHidden}
            label={'Next month'}
            onClick={() => handleMonthNavigation('next')}
          >
            <ChevronRight size={15} />
          </MonthArrow>
        )}
      </HStack>

      {isFetchingAvailability && <DateSelectionSkeleton />}

      {/* Date selection */}
      {!isFetchingAvailability && (
        <Box
          display={'grid'}
          gap={'4px'}
          gridTemplateColumns={'repeat(7, minmax(0, 1fr))'}
          w={'100%'}
        >
          {WEEKDAY_LABELS.map((label) => (
            <Text
              color={'blueGray.600'}
              fontSize={'10.5px'}
              fontWeight={700}
              key={label}
              letterSpacing={'0.03em'}
              pb={'4px'}
              textAlign={'center'}
            >
              {label}
            </Text>
          ))}

          {visibleDays.map((date, index) => {
            if (!date) return <Box key={`blank-${index}`} />;

            const hasTimeAvailable = !!getDateAvailability(availability, date)
              .length;
            const isDisabled =
              date < getEarliestSelectableDay() || !hasTimeAvailable;
            const isActive = selectedDay === date.toDateString();

            return (
              <Button
                bgColor={
                  isDisabled
                    ? 'transparent'
                    : isActive
                      ? 'secondary.500'
                      : 'feather.200'
                }
                borderWidth={0}
                borderRadius={'10px'}
                _hover={
                  isDisabled || isActive ? undefined : { bgColor: 'blue.100' }
                }
                aspectRatio={1}
                height={'auto'}
                minH={0}
                minW={0}
                paddingX={0}
                width={'100%'}
                key={date.toDateString()}
                onClick={() => !isDisabled && handleDateChange(date)}
                disabled={isDisabled}
                pointerEvents={isDisabled ? 'none' : 'auto'}
              >
                <Text
                  textAlign={'center'}
                  color={
                    isDisabled
                      ? DISABLED_COLOR
                      : isActive
                        ? 'white'
                        : 'secondary.500'
                  }
                  fontSize={'14px'}
                  fontWeight={isDisabled ? 400 : 700}
                >
                  {date.toLocaleDateString('en-US', {
                    day: 'numeric',
                  })}
                </Text>
              </Button>
            );
          })}
        </Box>
      )}

      {/* Month expand */}
      {(isMonthExpanded || canExpand) && (
        <HStack alignItems={'center'} w={'100%'}>
          <Box bg={'fieldBorder.200'} flex={1} h={'1px'} />

          <Pressable
            alignItems={'center'}
            aria-expanded={isMonthExpanded}
            aria-label={
              isMonthExpanded ? 'Show fewer dates' : 'Show more dates'
            }
            bg={'white'}
            borderColor={'fieldBorder.200'}
            borderWidth={1}
            color={'secondary.200'}
            h={'30px'}
            justifyContent={'center'}
            mx={'10px'}
            onClick={() => setIsMonthExpanded((isExpanded) => !isExpanded)}
            rounded={'pill'}
            w={'30px'}
          >
            {isMonthExpanded ? (
              <ChevronUp size={15} />
            ) : (
              <ChevronDown size={15} />
            )}
          </Pressable>

          <Box bg={'fieldBorder.200'} flex={1} h={'1px'} />
        </HStack>
      )}

      {/* Selected day */}
      <VStack alignItems={'flex-start'} gap={'5px'} w={'100%'}>
        <Text color={'secondary.500'} fontSize={'14px'} fontWeight={700}>
          {formatSelectedDate(selectedDate)}
        </Text>

        {!!timeZoneLabel && (
          <HStack alignItems={'center'} color={'blueGray.600'} gap={'7px'}>
            <Globe size={14} />
            <Text color={'blueGray.600'} fontSize={'12.5px'}>
              {timeZoneLabel}
            </Text>
          </HStack>
        )}
      </VStack>

      {/* Time slots */}
      <VStack alignItems={'stretch'} gap={'8px'} w={'100%'}>
        {visibleTimes.map(({ time, label }) => {
          const isoString = typeof time === 'string' ? time : time.time;
          const isSelected = selectedTime === time;

          return (
            <Button
              bgColor={isSelected ? 'secondary.200' : 'white'}
              borderColor={'secondary.200'}
              borderWidth={'1.5px'}
              _hover={isSelected ? undefined : { bgColor: 'feather.100' }}
              h={'48px'}
              key={isoString}
              minH={'48px'}
              onClick={() => onSelectTime(time)}
              rounded={'row'}
              w={'100%'}
            >
              <Text
                color={isSelected ? 'white' : 'secondary.200'}
                fontSize={'14px'}
                fontWeight={700}
              >
                {label}
              </Text>
            </Button>
          );
        })}
      </VStack>

      {!isShowingAllTimes && availableTimes.length > INITIAL_TIMES_COUNT && (
        <Button
          bgColor={'transparent'}
          h={'auto'}
          minH={'auto'}
          onClick={() => setIsShowingAllTimes(true)}
          py={0}
          variant={'ghost'}
          w={'100%'}
        >
          <Text
            color={'secondary.500'}
            fontSize={'14px'}
            fontWeight={600}
            textDecoration={'underline'}
          >
            Show more times
          </Text>
        </Button>
      )}
    </VStack>
  );
};
